/**
 * The thin web step from `lib/outreach-thread-view.ts`'s SHAPES to strings. Pure (no React), so the
 * components and the tests share it.
 *
 * Dates and times are the browser's locale formatting (`Intl`), never relative ("13w") — a thread is read for
 * when things happened. `{maestroAgentName}` is passed in by the caller (`useAgentName().name`); web's catalog
 * layer does not fill it.
 */
import type { DayLabel, ThreadLabel, TimelineTopic } from './outreach-thread-view';

export type Translate = (key: string, params?: Record<string, unknown>) => string;

export function renderThreadLabel(label: ThreadLabel, t: Translate, maestroAgentName: string): string {
  if (!label) return '';
  if ('text' in label) return label.text;
  return t(label.key, { maestroAgentName, ...label.params });
}

/** A divider / header day: the catalog's Today / Yesterday, else month + day (+ year when not this year). */
export function renderDayLabel(day: DayLabel, t: Translate, locale?: string, timeZone?: string): string {
  if (day.kind === 'today') return t('outreach.thread.today');
  if (day.kind === 'yesterday') return t('outreach.thread.yesterday');
  const date = new Date(day.at);
  if (!Number.isFinite(date.getTime())) return '';
  return date.toLocaleDateString(locale, { month: 'short', day: 'numeric', ...(day.sameYear ? {} : { year: 'numeric' }), timeZone });
}

/** "{name}'s agent started this · Jul 19" — the day joined with " · ", or the sentence alone. */
export function renderStartedBy(
  startedBy: { label: ThreadLabel; day: DayLabel | null },
  t: Translate,
  maestroAgentName: string,
  locale?: string,
  timeZone?: string,
): string {
  const sentence = renderThreadLabel(startedBy.label, t, maestroAgentName);
  const day = startedBy.day ? renderDayLabel(startedBy.day, t, locale, timeZone) : '';
  return day ? `${sentence} · ${day}` : sentence;
}

function validDate(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isFinite(date.getTime()) ? date : null;
}

/** The short time beside a bubble ("9:05 AM" / "09:05"). Empty when the message carries no readable time. */
export function messageClock(iso: string | null | undefined, locale?: string, timeZone?: string): string {
  const date = validDate(iso);
  return date ? date.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit', timeZone }) : '';
}

/** The exact instant — the hover title / accessibility label. */
export function messageExactTime(iso: string | null | undefined, locale?: string, timeZone?: string): string {
  const date = validDate(iso);
  return date ? date.toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short', timeZone }) : '';
}

/**
 * A timeline topic divider's title: "Lunch · Sep 8" — the thread's subject and the day it started — or, for a
 * thread with no subject, `fallbackTitle` alone (the caller's `threadTitle`, which is then already the
 * creation date and time, so the day is not repeated).
 */
export function renderTopicTitle(topic: TimelineTopic, fallbackTitle: string, t: Translate, locale?: string, timeZone?: string): string {
  if (!topic.subject) return fallbackTitle;
  const day = topic.day ? renderDayLabel(topic.day, t, locale, timeZone) : '';
  return day ? `${topic.subject} · ${day}` : topic.subject;
}
